import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Loader2, Files } from 'lucide-react';
import { supplierInvoiceDedupAPI, gmailGmailHashBackfillAPI } from '../../lib/api';
import { getErrorMessage } from '../../lib/errors';

const TIER_LABELS = {
  high_confidence: 'High confidence',
  medium_confidence: 'Medium confidence',
  ambiguous: 'Ambiguous (never auto-merged)',
  distinct_number_skipped: 'Distinct-number pairs skipped',
};

const REASON_LABELS = {
  number_match_amount_conflict: 'invoice number matches but amount does not',
  number_conflict: 'both sides have a distinct invoice number',
  fan_out: 'fan-out, no unique exact twin',
  number_absent_no_evidence: 'no invoice number, and no hash/message evidence',
};

const EVIDENCE_LABELS = {
  settlement_proven: 'settlement-proven (a transaction already reconciled the sibling)',
  attachment_hash: 'identical attachment (byte-for-byte same PDF)',
  message_id: 'identical Gmail message',
  number_amount_vendor: 'invoice number + amount + vendor match',
};

function GmailGmailPairRow({ row }) {
  return (
    <li className="font-mono">
      canonical {row.canonical_id} ({row.currency} {row.canonical_amount ?? '?'}, {row.canonical_invoice_number || '(no number)'})
      {' vs '}
      loser {row.loser_id} ({row.currency} {row.loser_amount ?? '?'}, {row.loser_invoice_number || '(no number)'})
      {row.evidence && (
        <> · {EVIDENCE_LABELS[row.evidence] || row.evidence}</>
      )}
      {row.reason && (
        <> · {REASON_LABELS[row.reason] || row.reason}</>
      )}
    </li>
  );
}

function PairRow({ row }) {
  return (
    <li className="font-mono">
      {row.supplier} · {row.currency} {row.gmail_amount ?? '?'} (gmail {row.gmail_invoice_number || '(no number)'})
      {' vs '}
      {row.currency} {row.xero_amount ?? '?'} (xero {row.xero_invoice_number || '(no number)'})
      {' · '}gmail {row.gmail_id} → xero {row.xero_id}
      {row.gmail_entity_id === 'unassigned' && row.xero_entity_id && (
        <> · would assign entity {row.xero_entity_id}</>
      )}
      {row.xero_payment_status === 'paid' && row.gmail_payment_status !== 'paid' && (
        <> · would mark Paid</>
      )}
      {row.reason && (
        <> · {REASON_LABELS[row.reason] || row.reason}</>
      )}
    </li>
  );
}

export function SupplierInvoiceDedupPanel() {
  const [preview, setPreview] = useState(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [previewFailed, setPreviewFailed] = useState(null);

  const [includeMedium, setIncludeMedium] = useState(false);
  const [applyBusy, setApplyBusy] = useState(false);
  const [applyResult, setApplyResult] = useState(null);
  const [applyFailed, setApplyFailed] = useState(null);

  const [ggPreview, setGgPreview] = useState(null);
  const [ggPreviewBusy, setGgPreviewBusy] = useState(false);
  const [ggPreviewFailed, setGgPreviewFailed] = useState(null);

  const [ggApplyBusy, setGgApplyBusy] = useState(false);
  const [ggApplyResult, setGgApplyResult] = useState(null);
  const [ggApplyFailed, setGgApplyFailed] = useState(null);

  const [backfillBusy, setBackfillBusy] = useState(false);
  const [backfillStatus, setBackfillStatus] = useState(null);
  const [backfillFailed, setBackfillFailed] = useState(null);

  const runPreview = async () => {
    setPreviewBusy(true);
    setPreviewFailed(null);
    setApplyResult(null);
    setApplyFailed(null);
    try {
      const { data } = await supplierInvoiceDedupAPI.preview();
      setPreview(data);
    } catch (err) {
      setPreviewFailed(getErrorMessage(err) || 'Failed to run preview.');
    } finally {
      setPreviewBusy(false);
    }
  };

  const mergeCount = preview
    ? (preview.tallies?.high_confidence || 0) + (includeMedium ? preview.tallies?.medium_confidence || 0 : 0)
    : 0;

  const runApply = async () => {
    if (!window.confirm(
      `Merge ${mergeCount} Xero-sourced supplier invoice duplicate(s) into their canonical Gmail twin now ` +
      `(${includeMedium ? 'high + medium confidence' : 'high confidence only'})? ` +
      'Ambiguous pairs are never auto-merged, and the Xero doc is never deleted.'
    )) return;
    setApplyBusy(true);
    setApplyFailed(null);
    setApplyResult(null);
    try {
      const { data } = await supplierInvoiceDedupAPI.apply(includeMedium);
      setApplyResult(data);
    } catch (err) {
      setApplyFailed(getErrorMessage(err) || 'Failed to apply.');
    } finally {
      setApplyBusy(false);
    }
  };

  const runGgPreview = async () => {
    setGgPreviewBusy(true);
    setGgPreviewFailed(null);
    setGgApplyResult(null);
    setGgApplyFailed(null);
    try {
      const { data } = await supplierInvoiceDedupAPI.previewGmailGmail();
      setGgPreview(data);
    } catch (err) {
      setGgPreviewFailed(getErrorMessage(err) || 'Failed to run preview.');
    } finally {
      setGgPreviewBusy(false);
    }
  };

  const ggMergeCount = ggPreview ? (ggPreview.tallies?.high_confidence || 0) : 0;

  const runGgApply = async () => {
    if (!window.confirm(
      `Merge ${ggMergeCount} gmail-gmail supplier invoice duplicate pair(s) now (high confidence only)? ` +
      'Ambiguous pairs are never auto-merged, and the losing doc is never deleted.'
    )) return;
    setGgApplyBusy(true);
    setGgApplyFailed(null);
    setGgApplyResult(null);
    try {
      const { data } = await supplierInvoiceDedupAPI.applyGmailGmail();
      setGgApplyResult(data);
    } catch (err) {
      setGgApplyFailed(getErrorMessage(err) || 'Failed to apply.');
    } finally {
      setGgApplyBusy(false);
    }
  };

  const runBackfillStart = async () => {
    setBackfillBusy(true);
    setBackfillFailed(null);
    try {
      await gmailGmailHashBackfillAPI.start();
      const { data } = await gmailGmailHashBackfillAPI.status();
      setBackfillStatus(data.chain);
    } catch (err) {
      setBackfillFailed(getErrorMessage(err) || 'Failed to start backfill.');
    } finally {
      setBackfillBusy(false);
    }
  };

  const refreshBackfillStatus = async () => {
    setBackfillBusy(true);
    setBackfillFailed(null);
    try {
      const { data } = await gmailGmailHashBackfillAPI.status();
      setBackfillStatus(data.chain);
    } catch (err) {
      setBackfillFailed(getErrorMessage(err) || 'Failed to fetch status.');
    } finally {
      setBackfillBusy(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <Files className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-base">Supplier Invoice Dedup</CardTitle>
        </div>
        <p className="text-xs text-muted-foreground">
          Gmail is canonical -- a Xero-sourced supplier invoice duplicate (same core-token vendor,
          amount and/or invoice number, dates within a window) merges INTO its Gmail twin: entity,
          Paid status, and the Xero link carry over onto the Gmail doc; the Xero doc is marked
          merged_duplicate (never deleted) and drops out of every list total and matcher population.
          Apply defaults to high-confidence pairs only; tick the box below to also merge
          medium-confidence pairs after eyeballing them. When one invoice fans out to several
          candidates, an exact number-and-amount twin among them wins automatically and the other
          candidates are dropped, never listed. An equal invoice number with a mismatched amount, or
          two distinct explicit invoice numbers on an otherwise matching pair, never merges -- both
          stay ambiguous with a reason shown on the row. Ambiguous pairs are listed only, never
          auto-merged.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-2">
          <Button
            variant="outline" size="sm" onClick={runPreview} disabled={previewBusy}
            data-testid="supplier-invoice-dedup-preview"
          >
            {previewBusy ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
            Preview
          </Button>
          {preview && !previewFailed && (
            <>
              <label className="flex items-center gap-1 text-xs text-muted-foreground">
                <input
                  type="checkbox"
                  checked={includeMedium}
                  onChange={(e) => setIncludeMedium(e.target.checked)}
                  data-testid="supplier-invoice-dedup-include-medium"
                />
                include medium confidence
              </label>
              <Button
                variant="default" size="sm" onClick={runApply} disabled={applyBusy || mergeCount === 0}
                data-testid="supplier-invoice-dedup-apply"
              >
                {applyBusy ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                Apply ({mergeCount})
              </Button>
            </>
          )}
        </div>

        {previewFailed && (
          <div
            className="p-3 rounded text-xs border bg-red-50 border-red-200 dark:bg-red-950/30 dark:border-red-900"
            data-testid="supplier-invoice-dedup-preview-error"
          >
            <p className="text-red-600 dark:text-red-400">{previewFailed}</p>
          </div>
        )}

        {preview && !previewFailed && (
          <div className="space-y-2" data-testid="supplier-invoice-dedup-preview-result">
            <div className="flex flex-wrap gap-2">
              {preview.tallies && Object.entries(preview.tallies).map(([key, count]) => (
                <Badge key={key} variant="outline" className="text-[10px]" data-testid={'supplier-invoice-dedup-tally-' + key}>
                  {TIER_LABELS[key] || key}: {count}
                </Badge>
              ))}
            </div>
            {preview.rows && Object.entries(preview.rows).map(([tier, bucket]) => (
              bucket.count > 0 && (
                <div key={tier}>
                  <p className="text-xs font-medium">{TIER_LABELS[tier] || tier} ({bucket.count})</p>
                  <ul className="text-xs space-y-1" data-testid={'supplier-invoice-dedup-rows-' + tier}>
                    {bucket.sample.map((row) => <PairRow key={row.gmail_id + '|' + row.xero_id} row={row} />)}
                  </ul>
                </div>
              )
            ))}
          </div>
        )}

        {applyFailed && (
          <div
            className="p-3 rounded text-xs border bg-red-50 border-red-200 dark:bg-red-950/30 dark:border-red-900"
            data-testid="supplier-invoice-dedup-apply-error"
          >
            <p className="text-red-600 dark:text-red-400">{applyFailed}</p>
          </div>
        )}

        {applyResult && !applyFailed && (
          <div
            className="p-3 rounded text-xs border bg-green-50 border-green-200 dark:bg-green-950/30 dark:border-green-900"
            data-testid="supplier-invoice-dedup-apply-result"
          >
            <p><strong>Merged:</strong> {applyResult.merged_count}</p>
          </div>
        )}

        <div className="pt-3 border-t space-y-3">
          <div>
            <p className="text-sm font-medium">Gmail-gmail dedup</p>
            <p className="text-xs text-muted-foreground">
              Two gmail-sourced docs for the SAME real invoice (a bulk-accountant-forward re-ingesting
              mail that already had a doc from the original sender). Merges ONLY on settlement-proof
              (a transaction already reconciled the sibling), an identical attachment (byte-for-byte
              same PDF), an identical Gmail message, or a matching invoice number + amount + vendor.
              Amount + vendor + date proximity alone never merges. The doc holding reconciliation/
              matching state is canonical; the loser is marked merged_duplicate, never deleted.
              Two distinct explicit invoice numbers at the same amount is the normal recurring-vendor
              pattern (e.g. a monthly subscription), not ambiguity -- those pairs are never merged and
              never listed, only counted in the "distinct-number pairs skipped" tally below.
            </p>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant="outline" size="sm" onClick={runGgPreview} disabled={ggPreviewBusy}
              data-testid="gmail-gmail-dedup-preview"
            >
              {ggPreviewBusy ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
              Preview
            </Button>
            {ggPreview && !ggPreviewFailed && (
              <Button
                variant="default" size="sm" onClick={runGgApply} disabled={ggApplyBusy || ggMergeCount === 0}
                data-testid="gmail-gmail-dedup-apply"
              >
                {ggApplyBusy ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                Apply ({ggMergeCount})
              </Button>
            )}
            <Button
              variant="outline" size="sm" onClick={runBackfillStart} disabled={backfillBusy}
              data-testid="gmail-gmail-hash-backfill-start"
            >
              {backfillBusy ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
              Backfill attachment hashes
            </Button>
            {backfillStatus && (
              <Button
                variant="ghost" size="sm" onClick={refreshBackfillStatus} disabled={backfillBusy}
                data-testid="gmail-gmail-hash-backfill-refresh"
              >
                Refresh status
              </Button>
            )}
          </div>

          {backfillFailed && (
            <div
              className="p-3 rounded text-xs border bg-red-50 border-red-200 dark:bg-red-950/30 dark:border-red-900"
              data-testid="gmail-gmail-hash-backfill-error"
            >
              <p className="text-red-600 dark:text-red-400">{backfillFailed}</p>
            </div>
          )}

          {backfillStatus && (
            <p className="text-xs text-muted-foreground" data-testid="gmail-gmail-hash-backfill-status">
              Backfill: {backfillStatus.status}
              {' · '}{backfillStatus.progress?.cohort_processed ?? 0} of {backfillStatus.metadata?.cohort_count ?? '?'} processed
            </p>
          )}

          {ggPreviewFailed && (
            <div
              className="p-3 rounded text-xs border bg-red-50 border-red-200 dark:bg-red-950/30 dark:border-red-900"
              data-testid="gmail-gmail-dedup-preview-error"
            >
              <p className="text-red-600 dark:text-red-400">{ggPreviewFailed}</p>
            </div>
          )}

          {ggPreview && !ggPreviewFailed && (
            <div className="space-y-2" data-testid="gmail-gmail-dedup-preview-result">
              <div className="flex flex-wrap gap-2">
                {ggPreview.tallies && Object.entries(ggPreview.tallies).map(([key, count]) => (
                  <Badge key={key} variant="outline" className="text-[10px]" data-testid={'gmail-gmail-dedup-tally-' + key}>
                    {TIER_LABELS[key] || key}: {count}
                  </Badge>
                ))}
              </div>
              {ggPreview.rows && Object.entries(ggPreview.rows).map(([tier, bucket]) => (
                bucket.count > 0 && (
                  <div key={tier}>
                    <p className="text-xs font-medium">{TIER_LABELS[tier] || tier} ({bucket.count})</p>
                    <ul className="text-xs space-y-1" data-testid={'gmail-gmail-dedup-rows-' + tier}>
                      {bucket.sample.map((row) => (
                        <GmailGmailPairRow key={row.canonical_id + '|' + row.loser_id} row={row} />
                      ))}
                    </ul>
                  </div>
                )
              ))}
            </div>
          )}

          {ggApplyFailed && (
            <div
              className="p-3 rounded text-xs border bg-red-50 border-red-200 dark:bg-red-950/30 dark:border-red-900"
              data-testid="gmail-gmail-dedup-apply-error"
            >
              <p className="text-red-600 dark:text-red-400">{ggApplyFailed}</p>
            </div>
          )}

          {ggApplyResult && !ggApplyFailed && (
            <div
              className="p-3 rounded text-xs border bg-green-50 border-green-200 dark:bg-green-950/30 dark:border-green-900"
              data-testid="gmail-gmail-dedup-apply-result"
            >
              <p><strong>Merged:</strong> {ggApplyResult.merged_count}</p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
